import React from "react";
import moment from "moment";
import { Link } from "react-router-dom";

import { Images, Utils } from "../../config";

import { boxContainer, Spacer, Charts, Buttons } from "../../components";

const General = (props) => {
  const data = props?.uniData;

  const profileData = [
    {
      icon: Images.Unigeneraltotalundergrad,
      title: "Total Undergraduate Students",
      hoverTitle:
        "Shows the total enrollment of undergraduate students at the university.",
      value: data?.totalUgStudents
        ? Utils.numWithCommas(data?.totalUgStudents)
        : "NA",
    },
    {
      icon: Images.Unigeneralavgclasssize,
      title: "Average Class Size",
      hoverTitle:
        "Represents the typical number of students in a class at the university.",
      value: data?.avgClassSize ?? "NA",
    },
    {
      icon: Images.Unigeneralfacultystudentratio,
      title: "Faculty to Student Ratio",
      hoverTitle:
        "Represents the number of faculty members available per student.",
      value: data?.facultyToStudentRatio ?? "NA",
    },
    {
      icon: Images.Unigeneralmaletofemale,
      title: "Male to Female Ratio",
      hoverTitle:
        "Shows the proportion of male to female students at the university.",
      value: data?.maleToFemaleRatio ?? "NA",
    },
  ];
  const rankingData = [
    {
      icon: Images.Unigeneralqs,
      title: "QS World",
      hoverTitle:
        "Displays the university's global ranking according to the QS World University Rankings.",
      value: data?.rankings?.QS ? `#${data?.rankings?.QS}` : "NA",
    },
    {
      icon: Images.Unigeneralcwur,
      title: "CWUR",
      hoverTitle:
        "Displays the university's global ranking according to the CWUR (Center for World University Rankings).",
      value: data?.rankings?.CWUR ? `#${data?.rankings?.CWUR}` : "NA",
    },
    {
      icon: Images.Unigeneraltimes,
      title: "Times",
      hoverTitle:
        "Displays the university's global ranking according to the Times Higher Education (THE) World University Rankings.",
      value: data?.rankings?.TIMES ? `#${data?.rankings?.TIMES}` : "NA",
    },
  ];
  const coreInfoDta = [
    {
      icon: Images.Unigeneralunitype,
      title: "University Type",
      hoverTitle:
        "Displays whether the university is a public or private institution.",
      value: data?.Type?.type ?? "NA",
    },
    {
      icon: Images.Unigeneralacceptancerate,
      title: "Acceptance Rate",
      hoverTitle:
        "Displays the percentage of applicants who are accepted by the university.",
      value: data?.acceptanceRate ? `${data?.acceptanceRate * 100}%` : "NA",
    },
    {
      icon: Images.Unigeneralavgcourseduration,
      title: "Average Course Duration",
      hoverTitle:
        "Displays the typical length of time required to complete a program at the university.",
      value: data?.courseDuration ?? "NA",
    },
    {
      icon: Images.Unigenerallocationtype,
      title: "Location Type",
      hoverTitle:
        "Indicates whether the university is located in an urban or rural area.",
      value: data?.LocationType?.locationType ?? "NA",
    },
    {
      icon: Images.Unigeneralresearchoutput,
      title: "Research Output",
      hoverTitle: "Indicates the level of research output at the university.",
      value: data?.researchOutput ?? "NA",
    },
  ];
  const pieChartData = [
    {
      label: "GAP Year or Seeking Employment",
      value: data?.placementRate ? 100 - data?.placementRate * 100 : null,
      color: "#d70d57",
    },
    {
      label: "Employed or in Graduate School",
      value: data?.placementRate ? data?.placementRate * 100 : null,
      color: "#3ad1bb",
    },
  ];
  const facilitiesAvailableData = [
    {
      icon: Images.Unigenerallibrary,
      title: "Library",
    },
    {
      icon: Images.Unigeneralprinting,
      title: "Printing",
    },
    {
      icon: Images.Unigeneralcafe,
      title: "Cafe",
    },
    {
      icon: Images.Unigeneralaid,
      title: "First Aid",
    },
  ];
  const publicTransportData = [
    {
      icon: Images.Unigeneraltrain,
      title: "Metro",
      hoverTitle: "Indicates the distance to the nearest metro station.",
      value: data?.trainDistance ? `${data?.trainDistance}km Away` : "NA",
    },
    {
      icon: Images.Unigeneralbus,
      title: "Bus",
      hoverTitle: "Indicates the distance to the nearest bus stop.",
      value: data?.busDistance ? `${data?.busDistance}km Away` : "NA",
    },
  ];
  return (
    <div>
      <div className="grid grid-cols-3 grid-rows-2 gap-[1.25em]">
        <div className="flex gap-2 flex-col row-span-2 h-full">
          <div className="box-title">Profile</div>
          <boxContainer.LiteBoxContainer addstyle="h-full">
            <div className="flex flex-col gap-4 py-3 px-2 justify-center h-full">
              {profileData?.map((item, index) => (
                <div
                  className={`box-content-container ${
                    index !== profileData?.length - 1 &&
                    "box-content-border pb-[1.25em]"
                  }`}
                  key={index}
                  title={item?.hoverTitle}
                >
                  <div className="box-icons-container">
                    <img src={item?.icon} className="w-[2.5em]" />
                  </div>
                  <div>
                    <div className="box-content-title">{item?.title}</div>
                    <div className="box-content-value">{item?.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex gap-2 flex-col row-span-1 col-span-1">
          <div className="box-title">Ranking</div>
          <boxContainer.LiteBoxContainer addstyle="h-full">
            <div className="flex justify-around items-center h-full flex-wrap">
              {rankingData?.map((item, index) => (
                <div
                  className="flex flex-col gap-2 items-center justify-center"
                  key={index}
                  title={item?.hoverTitle}
                >
                  <img src={item?.icon} className="h-[3.75em] w-[3.75em]" />
                  <div className="box-content-title pb-2 border-b-[2px] border-b-gray-300 w-full text-center">
                    {item.title}
                  </div>
                  <div className="box-content-value">{item.value}</div>
                </div>
              ))}
            </div>
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex gap-2 flex-col row-span-1 col-span-1">
          <div className="box-title">Post Graduation</div>
          <boxContainer.LiteBoxContainer addstyle="h-full">
            <div className="flex gap-2 items-center h-full">
              {data?.placementRate && (
                <div className="w-[22.5em] flex items-center justify-center">
                  <Charts.PieChartUniGeneral data={pieChartData} />
                </div>
              )}
              <div className="w-full">
                <div className="flex flex-col gap-1 pb-3 border-b-[2px] border-b-gray-300">
                  {pieChartData?.map((item, index) => (
                    <div
                      className="flex gap-2 items-center font-semibold text-[0.875em]"
                      key={index}
                    >
                      <div
                        style={{
                          backgroundColor: item.color,
                          width: "1.25em",
                          height: "0.625em",
                        }}
                      ></div>
                      {item.label}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 items-center pt-3">
                  <div className="h-[2em] w-[2em] flex justify-center items-center">
                    <img src={Images.Unigeneralavgannualgradsalary} />
                  </div>
                  <div>
                    <div className="text-[0.875em] font-semibold">
                      Average Annual Fresh Graduate Salary
                    </div>
                    <div className="box-content-value">
                      {data?.freshGradSalary
                        ? `USD ${data?.freshGradSalary?.toLocaleString()}`
                        : "NA"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex gap-2 flex-col row-span-1 col-span-2">
          <div className="box-title">Core Information</div>
          <boxContainer.LiteBoxContainer addstyle="h-full">
            <div className="flex justify-around items-center h-full">
              {coreInfoDta?.map((item, index) => (
                <div
                  className="flex flex-col gap-2 items-center justify-center"
                  key={index}
                  title={item?.hoverTitle}
                >
                  <img src={item?.icon} className="h-[3.75em] w-[3.75em]" />
                  <div className="box-content-title pb-2 border-b-[2px] border-b-gray-300 w-full text-center">
                    {item.title}
                  </div>
                  <div className="font-bold text-[1.125em]">{item.value}</div>
                </div>
              ))}
            </div>
          </boxContainer.LiteBoxContainer>
        </div>
      </div>
      <Spacer height={20} />
      <div className="grid grid-cols-4 grid-rows-2 gap-[1.25em]">
        <div className="flex gap-2 flex-col row-span-2 col-span-2">
          <div className="box-title">Student Life</div>
          <boxContainer.LiteBoxContainer addstyle="h-full">
            <div>
              <div className="flex justify-between items-center">
                <div className="font-semibold">Nearby Food Availibility</div>
                <div
                  className={`text-[0.875em] py-2 px-3 font-semibold flex gap-3 items-center`}
                  title="Indicates whether halal food options are available at the university."
                >
                  Halal Food Availibility:{" "}
                  <span
                    className={`text-white rounded-md px-2 py-0.5 ${
                      data?.halalFoodOption?.halalFoodOption === "Easy"
                        ? "bg-green-600"
                        : data?.halalFoodOption?.halalFoodOption === "Medium"
                        ? "bg-yellow-500 "
                        : "bg-red-600 "
                    }`}
                  >
                    {data?.halalFoodOption?.halalFoodOption}
                  </span>
                </div>
              </div>
              <Spacer height={20} />
              <div className="grid grid-cols-2 justify-center flex-wrap gap-x-[1.875em] gap-y-3">
                {data?.restaurants?.map((item, index) => (
                  <div key={index} className="flex gap-4 items-center">
                    <img
                      src={item?.imageURL ?? Images.Unigeneraldefaultrestaurant}
                      className="restaurant-image-container"
                    />
                    <div>
                      <div className="box-content-title">
                        {item?.restaurantName}
                      </div>
                      <div className="box-content-value">
                        {item?.distance}km Away
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex gap-2 flex-col row-span-2 col-span-1">
          <div className="box-title">Facilities Available</div>
          <boxContainer.LiteBoxContainer addstyle="h-full">
            <div className="flex flex-col gap-4 px-2 items-center justify-around h-full">
              <div className="flex flex-col gap-[1.563em] justify-center pl-[2.5em] mt-[1em] ml-[1.875em] mr-[1.25em] border-l-[2px] border-dashed border-l-gray-300 w-full">
                {facilitiesAvailableData?.map((item, index) => (
                  <div
                    className={`box-content-container justify-between relative pr-4 ${
                      index !== facilitiesAvailableData?.length - 1 &&
                      "box-content-border pb-[1.563em]"
                    }`}
                    key={index}
                  >
                    <img
                      src={Images.Unigeneralfacilities}
                      className="absolute h-[1.125em] w-[1.125em] left-[-3.125em] top-0"
                    />
                    <div className="box-icons-container">
                      <img src={item?.icon} className="w-[2.5em]" />
                    </div>

                    <div>
                      <div className="font-semibold">{item?.title}</div>
                    </div>
                    <div className="box-icons-container">
                      <img src={Images.GreenCheck} className="w-[1.5em]" />
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to={data?.facilitiesLink}
                target="_blank"
                className="w-[90%]"
              >
                <Buttons.DefaultButton width="full">
                  Check Out More
                </Buttons.DefaultButton>
              </Link>
            </div>
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex gap-2 flex-col row-span-1 col-span-1">
          <div className="box-title">Public Transport</div>
          <boxContainer.LiteBoxContainer addstyle="h-full">
            <div className="h-full flex flex-col justify-center">
              {publicTransportData?.map((item, index) => (
                <div
                  className={`box-content-container ${
                    index !== publicTransportData?.length - 1 &&
                    "box-content-border"
                  }`}
                  key={index}
                  title={item?.hoverTitle}
                >
                  <div className="box-icons-container">
                    <img src={item?.icon} className="w-[2.5em]" />
                  </div>
                  <div>
                    <div className="box-content-title">{item?.title}</div>
                    <div className="box-content-value">{item?.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </boxContainer.LiteBoxContainer>
        </div>
        <div className="flex gap-2 flex-col row-span-1 col-span-1">
          <div className="box-title">Application Deadline</div>
          <boxContainer.LiteBoxContainer addstyle="h-full">
            {data?.deadlines?.length === 0 ? (
              <div className="h-full flex flex-col justify-center">
                <div className={`box-content-container box-content-border`}>
                  <div className="box-icons-container">
                    <img
                      src={Images.Unigeneralappdeadline}
                      className="w-[2.5em]"
                    />
                  </div>
                  <div>
                    <div className="box-content-title">
                      Application Deadline
                    </div>
                    <div className="box-content-value">N/A</div>
                  </div>
                </div>
                <div className={`box-content-container`}>
                  <div className="box-icons-container">
                    <img
                      src={Images.Unigeneralappdeadline}
                      className="w-[2.5em]"
                    />
                  </div>
                  <div>
                    <div className="box-content-title">Result Date</div>
                    <div className="box-content-value">N/A</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3 px-2 justify-center">
                <div className={`box-content-container box-content-border`}>
                  <div className="box-icons-container">
                    <img
                      src={Images.Unigeneralappdeadline}
                      className="w-[2.5em]"
                    />
                  </div>
                  <div>
                    <div className="box-content-title">
                      {data?.deadlines[0]?.deadlineType ??
                        "Application Deadline"}
                    </div>
                    <div className="box-content-value">
                      {data?.deadlines[0]?.appDeadline
                        ? moment(data?.deadlines[0]?.appDeadline).format(
                            "DD MMMM, YYYY"
                          )
                        : "NA"}
                    </div>
                  </div>
                </div>
                <div className={`box-content-container `}>
                  <div className="box-icons-container">
                    <img
                      src={Images.Unigeneralappdeadline}
                      className="w-[2.5em]"
                    />
                  </div>
                  <div>
                    <div className="box-content-title">Result Date</div>
                    <div className="box-content-value">
                      {data?.deadlines[0]?.resultDate
                        ? moment(data?.deadlines[0]?.resultDate).format(
                            "DD MMMM, YYYY"
                          )
                        : "NA"}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </boxContainer.LiteBoxContainer>
        </div>
      </div>
    </div>
  );
};

export default General;
